<template>
    <aside class="left-menu"
        :class="{
            'left-menu-collapsed': isCollapsed,
        }">
            <div class="left-menu-brand" @click="goto('/dashboard')" aria-label="muven, ir para o dashboard">
                <span class="left-menu-trail" aria-hidden="true"><span></span><span></span><span></span></span>
                <img class="left-menu-logo" src="../../assets/logo-muven.png" alt="" width="36" height="36">
                <span class="left-menu-wordmark" aria-hidden="true">muven</span>
            </div>

            <nav class="left-menu-nav" aria-label="Menu principal">
                <div v-for="region in regions" :key="region.key"
                    :class="['left-menu-region', 'left-menu-region-' + region.key]"
                    @scroll.passive="closeHint()">
                    <span class="left-menu-indicator"
                        aria-hidden="true"
                        v-show="indicator.region === region.key"
                        :class="{'left-menu-indicator-ready': indicator.ready}"
                        :style="{
                            height: indicator.height + 'px',
                            transform: 'translateY(' + indicator.top + 'px)',
                            backgroundColor: indicator.color,
                        }"></span>

                    <div class="left-menu-section" v-for="section in region.sections" :key="section.key">
                        <p class="left-menu-section-title" aria-hidden="true" v-if="section.title && !section.pinned">
                            <span class="left-menu-section-title-text">{{section.title}}</span>
                        </p>

                        <ul class="left-menu-items-container" :aria-label="section.title">
                            <li class="left-menu-item"
                                v-for="item in section.items" :key="item.path"
                                :class="{active: item.active}"
                                :style="{'--item-color': item.color}"
                                v-show="item.isVisible()"
                                @mouseenter="onItemEnter($event, item)"
                                @mouseleave="onItemLeave()"
                                @focusin="onItemFocus($event, item)"
                                @focusout="closeHint()"
                                @click="closeHint()">
                                <router-link class="left-menu-item-container" :to="item.path" v-if="!item.external"
                                    :aria-label="isCollapsed ? item.description : null"
                                    :aria-describedby="hintItem === item ? hintId : null">
                                    <fa :icon="item.icon" class="left-menu-icon" />
                                    <span class="left-menu-item-description">{{item.description}}</span>
                                </router-link>

                                <a class="left-menu-item-container" :href="item.path" target="_blank" v-if="!!item.external"
                                    :aria-label="isCollapsed ? item.description : null"
                                    :aria-describedby="hintItem === item ? hintId : null">
                                    <fa :icon="item.icon" class="left-menu-icon" />
                                    <span class="left-menu-item-description">{{item.description}}</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <ul class="left-menu-items-container" v-if="region.key === 'footer'">
                        <li class="left-menu-item left-menu-item-collapse-action">
                            <button type="button" class="left-menu-item-container"
                                :aria-label="isCollapsed ? 'Expandir menu' : 'Recolher menu'"
                                :aria-expanded="String(!isCollapsed)"
                                :title="isCollapsed ? 'Expandir menu' : null"
                                @click="toggleCollapse()">
                                <fa icon="chevron-circle-left" class="left-menu-icon left-menu-collapse-icon" />
                                <span class="left-menu-item-description">Esconder</span>
                            </button>
                        </li>
                    </ul>
                </div>
            </nav>

            <MenuHintCmp ref="hint" :id="hintId" @change="hintItem = $event" />
    </aside>
</template>

<script>
import MenuHintCmp from '@/menu/components/MenuHintCmp'
import {createMenuSections, flattenMenuSections, activateMenuRoute} from '@/menu/MenuItems'

const HINT_DELAY_COLLAPSED = 300
const HINT_DELAY_EXPANDED = 700
const INDICATOR_MAX_HEIGHT = 22

export default {
    name: 'LeftMenuCmp',
    components: {
        MenuHintCmp,
    },
    data() {
        return {
            isCollapsed: false,
            hintId: 'muven-left-menu-hint',
            hintItem: null,
            canHover: true,
            indicator: {
                region: null,
                ready: false,
                top: 0,
                height: INDICATOR_MAX_HEIGHT,
                color: null,
            },
            menuSections: createMenuSections(),
        }
    },
    created() {
        if (window.matchMedia) {
            this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches
        }
    },
    mounted() {
        this._activateRoute(this.$route.path)
        window.addEventListener('resize', this._updateIndicator)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this._updateIndicator)
    },
    methods: {
        goto(path) {
            if (this.$route.path !== path) {
                this.$router.push(path);
            }
        },
        toggleCollapse() {
            this.closeHint()
            this.isCollapsed = !this.isCollapsed
            this.$emit('oncollapsed', {isCollapsed: this.isCollapsed})
        },
        onItemEnter(ev, item) {
            if (!this.canHover) {
                return
            }

            const delay = this.isCollapsed ? HINT_DELAY_COLLAPSED : HINT_DELAY_EXPANDED

            this.$refs.hint.requestOpen(ev.currentTarget, item, delay)
        },
        onItemLeave() {
            this.$refs.hint.requestClose()
        },
        onItemFocus(ev, item) {
            if (!this._isFocusVisible(ev.target)) {
                return
            }

            this.$refs.hint.open(ev.currentTarget, item)
        },
        closeHint() {
            this.$refs.hint.close()
        },
        _isFocusVisible(el) {
            try {
                return el.matches(':focus-visible')
            } catch (e) {
                return false
            }
        },
        _activateRoute(path) {
            const activeItem = activateMenuRoute(this.menuItems, path)

            this.$nextTick(() => {
                this._updateIndicator()

                if (activeItem) {
                    this._scrollActiveItemIntoView()
                }
            })
        },
        _getActiveItemEl() {
            return this.$el.querySelector('.left-menu-item.active')
        },
        _scrollActiveItemIntoView() {
            const itemEl = this._getActiveItemEl()

            if (itemEl && itemEl.scrollIntoView) {
                itemEl.scrollIntoView({block: 'nearest'})
            }
        },
        _updateIndicator() {
            const activeItem = this.menuItems.find(item => item.active)
            const itemEl = this._getActiveItemEl()

            if (!activeItem || !itemEl || !itemEl.offsetHeight) {
                this.indicator.region = null
                return
            }

            const regionEl = itemEl.closest('.left-menu-region')
            const height = Math.min(INDICATOR_MAX_HEIGHT, itemEl.offsetHeight - 12)
            const top = itemEl.getBoundingClientRect().top - regionEl.getBoundingClientRect().top + regionEl.scrollTop

            this.indicator.height = height
            this.indicator.top = top + (itemEl.offsetHeight - height) / 2
            this.indicator.color = activeItem.color
            this.indicator.region = regionEl.classList.contains('left-menu-region-footer') ? 'footer' : 'scroll'

            if (!this.indicator.ready) {
                window.requestAnimationFrame(() => {
                    this.indicator.ready = true
                })
            }
        },
    },
    computed: {
        menuItems() {
            return flattenMenuSections(this.menuSections)
        },
        regions() {
            return [
                {key: 'scroll', sections: this.menuSections.filter(section => !section.pinned)},
                {key: 'footer', sections: this.menuSections.filter(section => section.pinned)},
            ]
        },
    },
    watch: {
        $route(to, from) {
            this.closeHint()
            this._activateRoute(to.path)
        },
    },
}
</script>

<style lang="scss" scoped>
@import '../../utils/styles/constants.scss';

$menu-ease: cubic-bezier(.4, 0, .2, 1);
$menu-pop: cubic-bezier(.34, 1.56, .64, 1);

.left-menu {
    position: fixed;
    background-color: var(--surface-1);
    left: 0;
    top: 0;
    width: 200px;
    height: 100%;
    margin-right: 15px;
    z-index: 2;
    // abaixo de 992px a navegacao e a BottomMenuCmp; este scoped venceria o .muven-hidden-mobile
    display: none;
    flex-direction: column;
    transition: width .22s $menu-ease;

    @media(min-width: 992px) {
        display: flex;
    }

    &.left-menu-collapsed {
        width: 68px;

        .left-menu-brand .left-menu-wordmark {
            opacity: 0;
            transform: translateX(-4px);
            transition: opacity .1s ease, transform .1s ease;
        }

        .left-menu-section-title {
            .left-menu-section-title-text {
                opacity: 0;
                transition: opacity .1s ease;
            }

            &::after {
                opacity: 1;
            }
        }

        ul.left-menu-items-container {

           li.left-menu-item {
                .left-menu-item-description {
                    opacity: 0;
                    visibility: hidden;
                    transition: opacity .1s ease, visibility 0s linear .1s;
                }

                .left-menu-collapse-icon,
                .left-menu-item-container:hover .left-menu-collapse-icon {
                    transform: rotate(180deg);
                }
           }
        }
    }

    // mesmo lockup do institucional e do login: tile do foguete + "muven" em Bricolage Grotesque
    .left-menu-brand {
        flex-shrink: 0;
        position: relative;
        display: flex;
        align-items: center;
        gap: 10px;
        height: 56px;
        // centraliza o icone de 36px nos 68px da sidebar recolhida
        padding: 0 16px;
        // acima da nav para o rastro passar por cima do primeiro item
        z-index: 3;
        cursor: pointer;

        .left-menu-logo {
            position: relative;
            flex-shrink: 0;
            width: 36px;
            height: 36px;
            border-radius: 10px;
            box-shadow: 0 6px 14px -6px color-mix(in srgb, var(--shadow-ink) 45%, transparent);
            transition: transform .2s $menu-pop;
        }

        .left-menu-wordmark {
            font-family: 'Bricolage Grotesque', 'Source Sans Pro', sans-serif;
            font-size: 24px;
            font-weight: 800;
            line-height: 1;
            letter-spacing: -.03em;
            white-space: nowrap;
            // o clipping fica no texto (e nao no .left-menu-brand) para o rastro poder vazar
            min-width: 0;
            overflow: hidden;
            color: $color-primary;
            transition: opacity .15s ease .08s, transform .15s ease .08s;
        }

        &:hover .left-menu-logo {
            transform: scale(1.05) rotate(-3deg);
        }

        // rastro de exaustao no hover, o mesmo da tela de carregamento (public/index.html, .splash-trail)
        // em escala para o icone de 36px. Sai do canto inferior esquerdo, oposto ao nariz do foguete.
        // A animacao roda sempre e so a opacidade do container muda, para entrar e sair suave
        .left-menu-trail {
            position: absolute;
            top: 43px;
            left: 19px;
            width: 0;
            height: 0;
            pointer-events: none;
            opacity: 0;
            transform: rotate(135deg);
            transition: opacity .25s ease;

            span {
                position: absolute;
                left: 0;
                height: 2px;
                border-radius: 2px;
                background: linear-gradient(90deg, color-mix(in srgb, var(--brand) 55%, transparent), transparent);
                transform-origin: left center;
                animation: left-menu-trail 1.2s ease-out infinite;

                &:nth-child(1) { top: -6px; width: 17px; animation-delay: .15s; }
                &:nth-child(2) { top: -1px; width: 26px; }
                &:nth-child(3) { top: 4px;  width: 14px; animation-delay: .35s; }
            }
        }

        &:hover .left-menu-trail {
            opacity: 1;
        }
    }

    @keyframes left-menu-trail {
        0%   { opacity: 0; transform: scaleX(.2); }
        30%  { opacity: 1; }
        100% { opacity: 0; transform: scaleX(1); }
    }

    .left-menu-nav {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
    }

    .left-menu-region {
        position: relative;
    }

    .left-menu-region-scroll {
        flex: 1;
        min-height: 0;
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
        padding-bottom: 8px;
        scrollbar-width: thin;
        scrollbar-color: var(--tint-brand-border) transparent;

        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-thumb {
            background-color: var(--tint-brand-border);
            border-radius: 3px;
        }
    }

    .left-menu-region-footer {
        flex-shrink: 0;
        padding: 6px 0 8px;
        border-top: 1px solid var(--border-subtle);
    }

    .left-menu-section-title {
        position: relative;
        display: flex;
        align-items: flex-end;
        height: 24px;
        margin: 6px 0 2px;
        padding: 0 19px 4px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: .06em;
        text-transform: uppercase;
        color: var(--text-faint);
        white-space: nowrap;
        overflow: hidden;

        .left-menu-section-title-text {
            transition: opacity .15s ease .08s;
        }

        &::after {
            content: '';
            position: absolute;
            left: 24px;
            bottom: 8px;
            width: 20px;
            height: 1px;
            background-color: var(--tint-brand-border);
            opacity: 0;
            transition: opacity .15s ease;
        }
    }

    .left-menu-indicator {
        position: absolute;
        top: 0;
        left: 0;
        width: 3px;
        border-radius: 0 3px 3px 0;
        pointer-events: none;

        &.left-menu-indicator-ready {
            transition: transform .28s $menu-ease, height .28s $menu-ease, background-color .28s ease;
        }
    }

    ul.left-menu-items-container {
        list-style: none;
        padding-left: 0;
        margin-bottom: 0;

        li.left-menu-item {
            --item-color: var(--brand);

            margin: 1px 8px;

            &.left-menu-item-collapse-action {
                --item-color: var(--text-muted);
            }

            .left-menu-item-container {
                position: relative;
                isolation: isolate;
                display: flex;
                align-items: center;
                width: 100%;
                padding: 5px 10px 5px 11px;
                border: 0;
                border-radius: 10px;
                background: none;
                color: var(--text-soft);
                text-align: left;
                cursor: pointer;
                overflow: hidden;
                white-space: nowrap;
                transition: color .15s ease;

                @media(min-height: 760px) {
                    padding-top: 7px;
                    padding-bottom: 7px;
                }

                &::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    z-index: -1;
                    border-radius: inherit;
                    background-color: var(--item-color);
                    opacity: 0;
                    transition: opacity .15s ease;
                }

                &:hover {
                    text-decoration: none;
                    color: var(--text-strong);

                    &::before {
                        opacity: .08;
                    }

                    .left-menu-icon {
                        color: var(--item-color);
                        transform: translateY(-1px) scale(1.08);
                    }
                }

                &:focus {
                    outline: none;
                }

                &:focus-visible {
                    outline: 2px solid var(--item-color);
                    outline-offset: 1px;
                }
            }

            &.active .left-menu-item-container {
                color: var(--text-strong);
                font-weight: 600;

                &::before {
                    opacity: .11;
                }

                .left-menu-icon {
                    color: var(--item-color);
                }
            }

            .left-menu-icon {
                flex-shrink: 0;
                width: 30px;
                text-align: center;
                margin-right: 12px;
                font-size: 1.1em;
                color: var(--text-faint);
                transition: color .15s ease, transform .18s $menu-pop;
            }

            .left-menu-item-description {
                opacity: 1;
                visibility: visible;
                transition: opacity .15s ease .08s, visibility 0s;
            }
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .left-menu,
    .left-menu .left-menu-indicator.left-menu-indicator-ready,
    .left-menu .left-menu-section-title .left-menu-section-title-text,
    .left-menu .left-menu-section-title::after,
    .left-menu .left-menu-brand .left-menu-logo,
    .left-menu .left-menu-brand .left-menu-wordmark,
    .left-menu ul.left-menu-items-container li.left-menu-item .left-menu-icon {
        transition: none;
    }

    .left-menu ul.left-menu-items-container li.left-menu-item .left-menu-item-container:hover .left-menu-icon,
    .left-menu .left-menu-brand:hover .left-menu-logo {
        transform: none;
    }

    .left-menu .left-menu-brand .left-menu-trail {
        display: none;
    }
}
</style>
